// eslint-disable-next-line no-restricted-imports
import {CaptchaFlow} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/options.types';
import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';
import {
  bordersColor,
  complementaryColor,
  darkGrayColor,
  primaryColor,
} from '../../constants/colors';
import {mediaMinDesktop, mediaMinTablet} from '../../constants/customMedia';
import {
  fsL,
  fsM,
  fsS,
  fsXS,
  fwBold,
  fwMediumBold,
} from '../../constants/typography';

import {useShowToast} from '../../../Toast/useShowToast';
import {HelpCenterPageCommonProps} from '../../../pages/HelpCenter/types';
import SimpleButton from '../../Buttons/SimpleButton';
import Icon, {IconSize} from '../../Icon/Icon';
import {GenericIconName} from '../../Icon/IconName';
import {CloudflareTurnstileType} from '../ContactPage/ContactForm';

const Rectangle = styled.div`
  border-radius: 2px;
  padding: 34px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  @media ${mediaMinTablet} {
    border: 1px solid ${bordersColor};
  }
  @media ${mediaMinDesktop} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const TextContainer = styled.div`
  margin-right: 20px;
  margin-bottom: 15px;
  text-align: center;
  width: 100%;
  @media ${mediaMinDesktop} {
    text-align: left;
    max-width: 45%;
    margin-bottom: 0;
  }
`;

const Headline = styled.div`
  font-size: ${fsM}px;
  ${fwBold};
  color: ${primaryColor};
  line-height: 1.33;
  margin-bottom: 10px;
  @media ${mediaMinTablet} {
    font-size: ${fsL}px;
  }
`;

const Subline = styled.div`
  font-size: ${fsXS}px;
  ${fwMediumBold};
  color: ${darkGrayColor};
  line-height: 1.43;
  @media ${mediaMinTablet} {
    font-size: ${fsS}px;
  }
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  @media ${mediaMinDesktop} {
    width: auto;
  }
`;

const StyledIcon = styled(Icon)`
  [disabled] & {
    filter: contrast(0);
  }

  @media ${mediaMinTablet} {
    height: ${IconSize.M}px;
    width: ${IconSize.M}px;
    flex-basis: ${IconSize.M}px;
  }
`;

const VoteButton = styled(SimpleButton)`
  cursor: pointer;

  height: 48px;

  --background-color: ${complementaryColor};

  &[disabled] {
    pointer-events: none;
  }

  width: 115px;
  @media ${mediaMinDesktop} {
    width: 140px;
  }
  &:first-of-type {
    margin-right: 12px;
  }
`;

interface Props {
  translations: HelpCenterPageCommonProps['translations']['articlePage']['user_feedback'];
  errorTranslation: HelpCenterPageCommonProps['translations']['contactPage']['form']['feedback_error_text'];
  onVote: (vote: 'up' | 'down', captchaToken?: string) => Promise<unknown>;
  CloudflareTurnstile?: CloudflareTurnstileType;
}

const ArticleUserFeedback: FC<Props> = ({
  translations,
  onVote,
  CloudflareTurnstile,
  errorTranslation,
}) => {
  const showToast = useShowToast();
  const [hideCaptcha, setHideCaptcha] = useState(false);
  const [captchaCode, setCaptchaCode] = useState<string | undefined>(undefined);

  const onExpire = useCallback(() => {
    setCaptchaCode(undefined);
  }, []);

  const onVoteClick = async (vote: 'up' | 'down') => {
    setHideCaptcha(true);

    await onVote(vote, captchaCode)
      .then(() => {
        showToast({
          type: 'SUCCESS',
          content: (
            <ToastWrapper>
              <span>{translations.success_headline}</span>
              <span>{translations.subline}</span>
            </ToastWrapper>
          ),
        });
      })
      .catch(() => {
        showToast({
          type: 'ERROR',
          content: errorTranslation,
        });
      });

    setHideCaptcha(false);
    onExpire();
  };

  return (
    <>
      <Rectangle>
        <TextContainer>
          <Headline>{translations.headline}</Headline>
          <Subline>{translations.subline}</Subline>
        </TextContainer>
        <Buttons>
          <VoteButton
            buttonType="secondary"
            data-testid="UserFeedbackDownvoteButton"
            disabled={!captchaCode}
          >
            <StyledIcon
              onClick={() => onVoteClick('down')}
              size={IconSize.S}
              asset={GenericIconName.IcSmileySad}
            />
          </VoteButton>
          <VoteButton
            buttonType="secondary"
            data-testid="UserFeedbackUpvoteButton"
            disabled={!captchaCode}
          >
            <StyledIcon
              onClick={() => onVoteClick('up')}
              size={IconSize.S}
              asset={GenericIconName.IcSmileyHappy}
            />
          </VoteButton>
        </Buttons>
      </Rectangle>
      {CloudflareTurnstile && !hideCaptcha && (
        <CloudflareTurnstileWrapper>
          <CloudflareTurnstile
            onSuccess={setCaptchaCode}
            /**
             * Using the ADD_TO_BASKET flow here for the captcha siteKey as that is our "default" siteKey.
             * We Call it ADD_TO_BASKET siteKey, but in practice it is used as the default siteKey for all captcha flows (except AUTH).
             */
            type={CaptchaFlow.ADD_TO_BASKET}
            onExpire={onExpire}
            uniqueKey="articleUserFeedback"
          />
        </CloudflareTurnstileWrapper>
      )}
    </>
  );
};

export default ArticleUserFeedback;

const CloudflareTurnstileWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 34px;

  @media ${mediaMinTablet} {
    justify-content: unset;
    margin-block: 34px 0;
  }
`;

const ToastWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
